import React, { useContext } from "react";
import {
  Dialog,
  DialogContent,
  Box,
  Stepper,
  Step,
  StepLabel,
  IconButton,
  Typography,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AutoAwesomeIcon from "@mui/icons-material/AutoAwesome";
import { useAICalendar } from "contexts/AICalendarContext";
import Step1Goals from "./Step1Goals";
import Step2Topics from "./Step2Topics";
import Step3Schedule from "./Step3Schedule";
import Step4Generate from "./Step4Generate";
import Step5Review from "./Step5Review";
import Step6Finalize from "./Step6Finalize";
import AppContext from "AppContext";
import i18n from "i18";
import { planIncludesAI } from "utils/aiCredits";

/**
 * AI Content Calendar Wizard
 * Multi-step wizard for bulk content planning and generation
 * @param {Object} props - Component props
 * @param {boolean} props.open - Whether wizard is open
 * @param {Function} props.onClose - Function to close wizard
 * @param {Function} props.onComplete - Function called when wizard complete
 * @returns {JSX.Element} Content Calendar Wizard dialog
 */
// Steps that cost AI credits
const AI_STEPS = [1, 2, 4];

export default function ContentCalendarWizard({ open, onClose, onComplete }) {
  const { state, reset } = useAICalendar();
  const { metaData } = useContext(AppContext);
  const aiCredits = metaData?.ai_credits || null;
  // "No AI in your plan" comes from the plan itself, not the wallet allowance:
  // a paid plan sitting at 0 credits is "out of credits", not a plan limit.
  const currentPlan = metaData?.subscription?.package;
  const planHasNoAI = !!currentPlan && !planIncludesAI(currentPlan);

  const steps = [
    i18n.t("aiCalendarWizard.steps.goals"),
    i18n.t("aiCalendarWizard.steps.topics"),
    i18n.t("aiCalendarWizard.steps.schedule"),
    i18n.t("aiCalendarWizard.steps.generate"),
    i18n.t("aiCalendarWizard.steps.review"),
    i18n.t("aiCalendarWizard.steps.finalize"),
  ];

  const handleClose = () => {
    if (state.currentStep > 1 && state.currentStep < 6) {
      // eslint-disable-next-line no-alert
      const confirmed = window.confirm(
        i18n.t("aiCalendarWizard.confirmExit")
      );
      if (!confirmed) return;
    }
    // Always reset on close so the next "AI Calendar" opens a clean Step 1
    // instead of restoring a half-finished wizard from sessionStorage.
    reset();
    onClose();
  };

  const handleComplete = () => {
    reset();
    onComplete();
  };

  const renderStep = () => {
    switch (state.currentStep) {
      case 1:
        return <Step1Goals />;
      case 2:
        return <Step2Topics />;
      case 3:
        return <Step3Schedule />;
      case 4:
        return <Step4Generate />;
      case 5:
        return <Step5Review />;
      case 6:
        return <Step6Finalize onComplete={handleComplete} />;
      default:
        return null;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          height: "90vh",
          maxHeight: "900px",
          bgcolor: "var(--background-menu)",
          color: "var(--text-color)",
          backgroundImage: "none",
        },
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          p: 2,
          borderBottom: 1,
          borderColor: "var(--border-color)",
          color: "var(--text-color)",
        }}
      >
        <Typography variant="h5">
          {i18n.t("aiCalendarWizard.title")}
        </Typography>
        <IconButton
          onClick={handleClose}
          size="small"
          sx={{ color: "var(--text-color)" }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <Box
        sx={{
          px: 3,
          py: 2,
          borderBottom: 1,
          borderColor: "var(--border-color)",
        }}
      >
        <Stepper activeStep={state.currentStep - 1}>
          {steps.map((label, index) => (
            <Step key={index}>
              <StepLabel
                sx={{
                  "& .MuiStepLabel-label": { color: "var(--text-secondary)" },
                  "& .MuiStepLabel-label.Mui-active": { color: "var(--text-color)" },
                  "& .MuiStepLabel-label.Mui-completed": { color: "var(--text-color)" },
                }}
              >
                {label}
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>

      <DialogContent
        sx={{
          p: 3,
          overflowY: "auto",
          display: "flex",
          flexDirection: "column",
          gap: 2,
          color: "var(--text-color)",
        }}
      >
        {/* Credits badge for AI steps */}
        {AI_STEPS.includes(state.currentStep) && aiCredits && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            {planHasNoAI ? (
              <Typography variant="caption" sx={{ color: "#FE8484" }}>
                {i18n.t("aiCredits.noCreditsInPlan")}
              </Typography>
            ) : (
              <Chip
                icon={<AutoAwesomeIcon sx={{ fontSize: "13px !important" }} />}
                label={`${i18n.t("aiCredits.remaining")}: ${aiCredits.monthly_remaining} / ${aiCredits.monthly_allowance}`}
                size="small"
                sx={{
                  backgroundColor:
                    aiCredits.monthly_remaining === 0 ? "#FE848420" : "#42C0C720",
                  color: aiCredits.monthly_remaining === 0 ? "#FE8484" : "#42C0C7",
                  border: `1px solid ${aiCredits.monthly_remaining === 0 ? "#FE848460" : "#42C0C760"}`,
                  fontSize: 11,
                  "& .MuiChip-icon": {
                    color: aiCredits.monthly_remaining === 0 ? "#FE8484" : "#42C0C7",
                  },
                }}
              />
            )}
          </Box>
        )}
        {renderStep()}
      </DialogContent>
    </Dialog>
  );
}
